import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  addEdge, useNodesState, useEdgesState, Controls, Background, MiniMap,
  MarkerType, Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import DashboardLayout from '../layouts/DashboardLayout';
import { workflowApi } from '../lib/api';
import { nodeTypes } from '../components/workflow/CustomNodes';
import NodePalette from '../components/workflow/NodePalette';
import PropertiesPanel from '../components/workflow/PropertiesPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, FloppyDisk, CircleNotch, Trash, Plus, ListBullets } from '@phosphor-icons/react';
import { toast } from 'sonner';

const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  style: { stroke: '#3b82f6', strokeWidth: 2 },
  animated: false,
};

const defaultNewNodes = [
  { id: 'start-1', type: 'start', position: { x: 80, y: 250 }, data: { label: 'Start' } },
  { id: 'end-1', type: 'end', position: { x: 700, y: 250 }, data: { label: 'End' } },
];

let nodeId = 10;
const getNextId = (type) => `${type}-${Date.now()}-${nodeId++}`;

export default function WorkflowDesignerPage() {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assignProductId = searchParams.get('assignProduct') || '';
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNewNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const [templateName, setTemplateName] = useState(searchParams.get('name') || '');
  const [templateDesc, setTemplateDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!templateId);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  useEffect(() => {
    if (templateId) loadTemplate();
    else {
      setSavedSnapshot(JSON.stringify({
        name: (searchParams.get('name') || '').trim(),
        description: '',
        nodes: [],
        edges: [],
      }));
    }
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    try {
      const res = await workflowApi.getTemplate(templateId);
      const tpl = res.data;
      const nextNodes = tpl.nodes || defaultNewNodes;
      const nextEdges = (tpl.edges || []).map((e) => ({ ...e, ...defaultEdgeOptions, label: e.data?.label || e.label }));
      setTemplateName(tpl.name || '');
      setTemplateDesc(tpl.description || '');
      setNodes(nextNodes);
      setEdges(nextEdges);
      setSavedSnapshot(JSON.stringify({
        name: (tpl.name || '').trim(),
        description: (tpl.description || '').trim(),
        nodes: nextNodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
        edges: nextEdges.map((e) => ({
          id: e.id, source: e.source, target: e.target,
          sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, data: e.data, label: e.label,
        })),
      }));
    } catch { toast.error('Failed to load template'); }
    finally { setLoading(false); }
  };

  const currentSnapshot = JSON.stringify({
    name: templateName.trim(),
    description: templateDesc.trim(),
    nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
    edges: edges.map((e) => ({
      id: e.id, source: e.source, target: e.target,
      sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, data: e.data, label: e.label,
    })),
  });
  const isDirty = savedSnapshot != null && currentSnapshot !== savedSnapshot;

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions, data: {} }, eds));
  }, [setEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    if (!type || !reactFlowInstance) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    const defaultLabels = {
      task: 'New Task',
      exclusive: 'Choice',
      parallel: 'Split',
      sync: 'Sync',
      merge: 'Merge',
      start: 'Start',
      end: 'End',
    };

    const newNode = {
      id: getNextId(type),
      type,
      position,
      data: { label: defaultLabels[type] || type },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [reactFlowInstance, setNodes]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const onUpdateNode = useCallback((nodeId, data) => {
    setNodes((nds) => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n));
    setSelectedNode(null);
    toast.success('Node updated');
  }, [setNodes]);

  const onUpdateEdge = useCallback((edgeId, data) => {
    setEdges((eds) => eds.map(e => e.id === edgeId ? { ...e, data: { ...e.data, ...data }, label: data.label || '' } : e));
    setSelectedEdge(null);
    toast.success('Edge updated');
  }, [setEdges]);

  const onDeleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter(n => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    toast.success('Node deleted');
  }, [setNodes, setEdges]);

  const onDeleteEdge = useCallback((edgeId) => {
    setEdges((eds) => eds.filter(e => e.id !== edgeId));
    setSelectedEdge(null);
    toast.success('Edge deleted');
  }, [setEdges]);

  const handleSave = async () => {
    if (!templateName.trim()) { toast.error('Enter a workflow name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: templateName.trim(),
        description: templateDesc.trim(),
        nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, data: e.data, label: e.label })),
      };
      let savedId = templateId;
      if (templateId) {
        await workflowApi.updateTemplate(templateId, payload);
        toast.success('Workflow updated');
      } else {
        const res = await workflowApi.createTemplate(payload);
        savedId = res.data.id;
        toast.success('Workflow saved');
      }
      if (assignProductId && savedId) {
        await workflowApi.assignToProduct(assignProductId, savedId);
        toast.success('Workflow assigned to product');
        navigate('/products');
      } else if (!templateId && savedId) {
        navigate(`/workflows/canvas/${savedId}`, { replace: true });
      }
      setSavedSnapshot(JSON.stringify({
        name: payload.name,
        description: payload.description,
        nodes: payload.nodes,
        edges: payload.edges,
      }));
      setSaveDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="workflow-designer-page">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/workflows')} className="text-zinc-400 hover:text-zinc-100 p-1 rounded hover:bg-zinc-800 transition-colors" data-testid="back-to-workflows">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">{templateName || 'New Workflow'}</h2>
              <p className="text-[10px] text-zinc-500">
                {assignProductId ? 'Will assign to product after save' : templateId ? `${nodes.length} nodes, ${edges.length} edges` : 'Unsaved'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {templateId && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/workflows/${templateId}`)}
                className="border-zinc-700 text-zinc-300 text-xs" data-testid="open-simple-builder">
                <ListBullets className="w-4 h-4 mr-1.5" /> Simple builder
              </Button>
            )}
            <Button size="sm" onClick={() => setSaveDialogOpen(true)} disabled={Boolean(templateId) && !isDirty} className="bg-brand-600 hover:bg-brand-700 text-white text-xs" data-testid="save-workflow-btn">
              <FloppyDisk className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </div>
        </div>

        {/* Main Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Palette */}
          <div className="w-52 shrink-0 border-r border-zinc-800 bg-zinc-900/50 p-3 overflow-y-auto">
            <NodePalette />
          </div>

          {/* Canvas */}
          <div className="flex-1" ref={reactFlowWrapper}>
            {loading ? (
              <div className="flex items-center justify-center h-full"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
            ) : (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                fitView
                proOptions={{ hideAttribution: true }}
                className="bg-zinc-950"
              >
                <Controls className="!bg-zinc-800 !border-zinc-700 !rounded-lg [&>button]:!bg-zinc-800 [&>button]:!border-zinc-700 [&>button]:!text-zinc-300 [&>button:hover]:!bg-zinc-700" />
                <Background color="#27272a" gap={20} size={1} />
                <MiniMap
                  nodeColor={(n) => {
                    if (n.type === 'start') return '#10b981';
                    if (n.type === 'end') return '#ef4444';
                    if (n.type === 'task') return '#3b82f6';
                    if (n.type === 'exclusive') return '#f59e0b';
                    if (n.type === 'parallel') return '#8b5cf6';
                    if (n.type === 'sync') return '#06b6d4';
                    if (n.type === 'merge') return '#ec4899';
                    return '#71717a';
                  }}
                  className="!bg-zinc-900 !border-zinc-800"
                  maskColor="rgba(0,0,0,0.6)"
                />
              </ReactFlow>
            )}
          </div>

          {/* Right Properties */}
          <div className="w-60 shrink-0 border-l border-zinc-800 bg-zinc-900/50 p-3 overflow-y-auto">
            <PropertiesPanel
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              onUpdateNode={onUpdateNode}
              onUpdateEdge={onUpdateEdge}
              onDeleteNode={onDeleteNode}
              onDeleteEdge={onDeleteEdge}
              onClose={() => { setSelectedNode(null); setSelectedEdge(null); }}
            />
          </div>
        </div>

        {/* Save Dialog */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{templateId ? 'Update Workflow' : 'Save Workflow'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-xs text-zinc-400">Name *</label>
                <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="workflow-name-input" />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-zinc-400">Description</label>
                <Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="workflow-desc-input" />
              </div>
              <div className="text-xs text-zinc-600">
                {nodes.length} nodes, {edges.length} edges
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)} className="border-zinc-700 text-zinc-300">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !templateName.trim()} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-save-workflow">
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
